/**
 * Pure utility functions for auto-detecting filterable dimensions
 * from chart data and applying client-side filters.
 */

/**
 * Inspects all charts' configs and data to find dimension columns that are
 * not used as index/value/category keys — these become filter candidates.
 *
 * @param {Object} chartsData - { [chartId]: { config, data, ... } }
 * @returns {Array<{ key: string, type: 'string'|'number', values: Array }>}
 */
export function extractFilterDefinitions(chartsData) {
  const charts = Object.values(chartsData).filter(c => c?.config && c?.data?.length);

  // Step 1: Build the global set of metric keys (value_keys / value_key across ALL charts).
  // A key that is a metric in ANY chart must never become a filter.
  const globalMetricKeys = new Set();
  charts.forEach(chart => {
    const { value_keys = [], value_key } = chart.config;
    value_keys.forEach(k => globalMetricKeys.add(k));
    if (value_key) globalMetricKeys.add(value_key);
  });

  // Step 2: Collect unique values for every non-metric key found in chart data.
  // This includes index_key (chain_id, hora_local) and any extra dimension columns
  // (network_type, etc.) — as long as they are not metrics.
  const globalValuesMap = {};

  charts.forEach(chart => {
    const sampleRow = chart.data[0];
    const filterableKeys = Object.keys(sampleRow).filter(
      k => !globalMetricKeys.has(k)
    );

    filterableKeys.forEach(key => {
      if (!globalValuesMap[key]) globalValuesMap[key] = new Set();
      chart.data.forEach(row => {
        const v = row[key];
        if (v !== undefined && v !== null && v !== '') {
          globalValuesMap[key].add(v);
        }
      });
    });
  });

  return Object.entries(globalValuesMap)
    .filter(([, values]) => values.size > 0)
    .map(([key, valuesSet]) => {
      const values = Array.from(valuesSet);
      const isNumeric = values.every(v => typeof v === 'number' || (!isNaN(Number(v)) && v !== ''));
      const type = isNumeric ? 'number' : 'string';

      if (isNumeric) {
        values.sort((a, b) => Number(a) - Number(b));
      } else {
        values.sort((a, b) => String(a).localeCompare(String(b)));
      }

      return { key, type, values };
    })
    .sort((a, b) => a.key.localeCompare(b.key));
}

/**
 * Re-aggregates rows by index key after filtering.
 * Supports "sum" and "avg" per metric as declared in config.aggregations.
 *
 * @param {Array} data - Filtered rows (already filtered by chain or other dimension)
 * @param {string} indexKey - The column to group by (e.g. "hora_local", "dia_semana")
 * @param {Object} aggregations - { [metric]: "sum" | "avg" }
 * @returns {Array} One row per unique indexKey value
 */
function aggregateByIndex(data, indexKey, aggregations) {
  const grouped = new Map();

  data.forEach(row => {
    const key = row[indexKey];
    if (!grouped.has(key)) grouped.set(key, { [indexKey]: key, __counts__: {} });
    const group = grouped.get(key);

    Object.entries(aggregations).forEach(([metric, aggType]) => {
      const val = row[metric];
      if (val === null || val === undefined) return;
      if (aggType === 'sum') {
        group[metric] = (group[metric] ?? 0) + Number(val);
      } else if (aggType === 'avg') {
        group[metric] = (group[metric] ?? 0) + Number(val);
        group.__counts__[metric] = (group.__counts__[metric] ?? 0) + 1;
      }
    });
  });

  return Array.from(grouped.values()).map(group => {
    Object.entries(group.__counts__).forEach(([metric, count]) => {
      group[metric] = count > 0 ? group[metric] / count : null;
    });
    delete group.__counts__;
    return group;
  });
}

/**
 * Filters each chart's data rows based on activeFilters, then re-aggregates
 * charts that declare config.aggregations (temporal charts with chain_id in data).
 * Returns the same shape as chartsData. If no filters are active and no chart
 * has aggregations, returns the original reference for referential equality.
 *
 * @param {Object} chartsData - { [chartId]: { config, data, ... } }
 * @param {Object} activeFilters - { [key]: selectedValues[] }
 * @returns {Object} filtered (and re-aggregated where applicable) chartsData
 */
export function applyFilters(chartsData, activeFilters) {
  const activeEntries = Object.entries(activeFilters).filter(
    ([, v]) => v?.length > 0
  );
  const hasAggregatedCharts = Object.values(chartsData).some(
    c => c?.config?.aggregations
  );

  if (activeEntries.length === 0 && !hasAggregatedCharts) return chartsData;

  const result = {};

  Object.entries(chartsData).forEach(([chartId, chart]) => {
    if (!chart?.data || !chart?.config) {
      result[chartId] = chart;
      return;
    }

    const { aggregations, index_key } = chart.config;

    // Step 1: filter rows
    let data = chart.data;
    if (activeEntries.length > 0) {
      data = data.filter(row =>
        activeEntries.every(([key, vals]) =>
          !(key in row) || vals.some(v => v == row[key])
        )
      );
    }

    // Step 2: re-aggregate if chart declares aggregations
    if (aggregations && index_key && data.length > 0) {
      data = aggregateByIndex(data, index_key, aggregations);
    }

    result[chartId] = { ...chart, data };
  });

  return result;
}
